import { useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PosiedzenieType } from '@/types/enums';
import type { CreatePosiedzenieRequest, PosiedzenieDto, SprawaDto } from '@/types';
import { useListSprawyQuery } from '@/store/sprawyApi';

interface PosiedzenieFormModalProps {
  show: boolean;
  onClose: () => void;
  onSubmit: (sprawaId: string, data: CreatePosiedzenieRequest) => Promise<void>;
  initialData?: PosiedzenieDto | null;
  sprawaId?: string;
  initialDate?: string;
}

interface FormValues {
  sprawaId: string;
  date: string;
  type: PosiedzenieType;
  location: string;
  notes: string;
}

function toDateTimeLocal(isoDate: string): string {
  const d = new Date(isoDate);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

const EMPTY: FormValues = {
  sprawaId: '',
  date: '',
  type: PosiedzenieType.Rozprawa,
  location: '',
  notes: '',
};

export default function PosiedzenieFormModal({
  show,
  onClose,
  onSubmit,
  initialData,
  sprawaId,
  initialDate,
}: PosiedzenieFormModalProps) {
  const { t } = useTranslation();
  const needSprawaSelector = !sprawaId;
  const { data: sprawyData } = useListSprawyQuery({ pageSize: 200 }, { skip: !needSprawaSelector || !show });

  const { register, handleSubmit, reset, formState: { isSubmitting } } = useForm<FormValues>({
    defaultValues: EMPTY,
  });

  useEffect(() => {
    if (!show) return;
    if (initialData) {
      reset({
        sprawaId: sprawaId ?? initialData.sprawaId,
        date: toDateTimeLocal(initialData.date),
        type: initialData.type,
        location: initialData.location ?? '',
        notes: initialData.notes ?? '',
      });
    } else {
      reset({
        ...EMPTY,
        sprawaId: sprawaId ?? '',
        date: initialDate ? toDateTimeLocal(initialDate) : '',
      });
    }
  }, [show, initialData, sprawaId, initialDate, reset]);

  const submit = handleSubmit(async (values) => {
    const targetSprawaId = sprawaId ?? values.sprawaId;
    if (!targetSprawaId || !values.date) return;
    await onSubmit(targetSprawaId, {
      date: new Date(values.date).toISOString(),
      type: values.type,
      location: values.location || undefined,
      notes: values.notes || undefined,
    });
    onClose();
  });

  const isEditing = !!initialData;
  const title = isEditing ? t('posiedzenia.editPosiedzenie') : t('posiedzenia.addPosiedzenie');

  return (
    <Modal show={show} onHide={onClose} centered>
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {needSprawaSelector && (
            <Form.Group className="mb-3">
              <Form.Label>{t('posiedzenia.form.sprawa')}</Form.Label>
              <Form.Select {...register('sprawaId', { required: true })}>
                <option value="">{t('posiedzenia.form.selectSprawa')}</option>
                {(sprawyData?.items ?? []).map((s: SprawaDto) => (
                  <option key={s.id} value={s.id}>
                    {s.signature ?? s.id}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>{t('posiedzenia.form.date')}</Form.Label>
            <Form.Control type="datetime-local" {...register('date', { required: true })} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('posiedzenia.form.type')}</Form.Label>
            <Form.Select {...register('type')}>
              {Object.values(PosiedzenieType).map((type) => (
                <option key={type} value={type}>
                  {t(`posiedzenieType.${type}`)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('posiedzenia.form.location')}</Form.Label>
            <Form.Control maxLength={200} {...register('location')} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>{t('posiedzenia.form.notes')}</Form.Label>
            <Form.Control as="textarea" rows={3} maxLength={2000} {...register('notes')} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isEditing ? t('common.save') : t('common.create')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
